import lodash from 'lodash';
import moment from 'moment';

import { nodeWithArgs } from 'common/util/queryHelper';
import { reportTypes, statusKey, DATE_FORMAT } from './constants';
import styles from '../containers/ReportContent.scss';

export const binderReportColumns = { 
    createdAt: { key: 'createdAt', label: 'Created Date', filter: 'Filter date' },
    createdBy: { key: 'createdBy', label: 'Created By', filter: 'Filter created by' },
    title: { key: 'title', label: 'Binder Name', filter: 'Filter name' },
    binderTemplateName: { key: 'binderTemplateName', label: 'Binder Template', filter: 'Filter template' },
    activationDate: { key: 'activationDate', label: 'Activation Date', filter: 'Filter date' },
    status: { key: 'status', label: 'Status', filter: 'Filter status' },
    primaryInvestigator: { key: 'primaryInvestigator', label: 'Primary Investigator', filter: 'Filter P.I.' },
    sponsorCro: { key: 'sponsorCro', label: 'Sponsor', filter: 'Filter sponsor' },
    protocolNumber: { key: 'protocolNumber', label: 'Protocol Number', filter: 'Filter protocol' },
    irbNumber: { key: 'irbNumber', label: 'IRB', filter: 'Filter IRB' },
    renewalDate: { key: 'renewalDate', label: 'Renewal Date', filter: 'Filter date' },
    binderType: { key: 'binderType', label: 'Binder Type', filter: 'Filter type' },
    binderForwardingAddress: {
        key: 'binderForwardingAddress',
        label: 'Binder Forwarding Address',
        filter: 'Filter address',
    },
    studyStage: {key: 'studyStage', label: 'Study Stage', filter: 'Filter Study Stage'}
};

export const binderReportDefaultColumns = [
    binderReportColumns.title.key,
    binderReportColumns.binderType.key,
    binderReportColumns.activationDate.key,
    binderReportColumns.status.key,
    binderReportColumns.protocolNumber.key,
    binderReportColumns.renewalDate.key,
];

const binderReportColumnValues = lodash.values(binderReportColumns);
export const binderReportColumnsList = lodash.orderBy(binderReportColumnValues, ['label'], ['asc']);

export const getBindersReportNode = (institutionId, statuses = undefined) => {
    return nodeWithArgs('getBindersReportRawData', {
        institutionId,
        statuses
    }, [
        'id',
        'createdAt',
        'createdBy',
        'title',
        'binderTemplateName',
        'activationDate',
        'status',
        'primaryInvestigator',
        'sponsorCro',
        'irbNumber',
        'renewalDate',
        'binderType',
        'binderForwardingAddress',
        'protocolNumber',
        'studyStage'
    ]);
};

export function renderBindersColumns(binders, customizeColumns) {
    if (lodash.isEmpty(binders) || lodash.isEmpty(customizeColumns)) {
        return [];
    }
    const lookups = getBinderStatusLookups(binders);
    const formattedColumns = formatMaterialTableBindersColumns(lookups, customizeColumns);

    return [...formattedColumns];
}

const getBinderStatusLookups = binders => {
    const binderStatuses = new Set();
    binders.forEach(binder => binderStatuses.add(binder.status));
    const lookups = lodash.reduce(
        [...binderStatuses],
        (obj, status) => {
            obj[status] = status;
            return obj;
        },
        {},
    );

    return lookups;
};

const formatMaterialTableBindersColumns = (binderStatusLookups, customizeColumns) => {
    return [
        {
            title: binderReportColumns.title.label,
            field: binderReportColumns.title.key,
            filterPlaceholder: binderReportColumns.title.filter,
            hidden: !customizeColumns.includes(binderReportColumns.title.key),
            render: rowData => <p className={styles.reportTitle}>{rowData[binderReportColumns.title.key]}</p>,
        },
        {
            title: binderReportColumns.binderType.label,
            field: binderReportColumns.binderType.key,
            filterPlaceholder: binderReportColumns.binderType.filter,
            hidden: !customizeColumns.includes(binderReportColumns.binderType.key),
            emptyValue: <p className={styles.emptyLabel}>No B.T Provided</p>,
        },
        {
            title: binderReportColumns.binderTemplateName.label,
            field: binderReportColumns.binderTemplateName.key,
            filterPlaceholder: binderReportColumns.binderTemplateName.filter,
            hidden: !customizeColumns.includes(binderReportColumns.binderTemplateName.key),
        },
        {
            title: binderReportColumns.createdAt.label,
            field: binderReportColumns.createdAt.key,
            filterPlaceholder: binderReportColumns.createdAt.filter,
            hidden: !customizeColumns.includes(binderReportColumns.createdAt.key),
            render: rowData => {
                return moment(rowData[binderReportColumns.createdAt.key]).format(DATE_FORMAT);
            },
        },
        {
            title: binderReportColumns.activationDate.label,
            field: binderReportColumns.activationDate.key,
            filterPlaceholder: binderReportColumns.activationDate.filter,
            hidden: !customizeColumns.includes(binderReportColumns.activationDate.key),
            emptyValue: <p className={styles.emptyLabel}>No A.D Provided</p>,
            render: rowData => {
                return moment(rowData[binderReportColumns.activationDate.key]).format(DATE_FORMAT);
            },
        },
        {
            title: binderReportColumns.createdBy.label,
            field: binderReportColumns.createdBy.key,
            filterPlaceholder: binderReportColumns.createdBy.filter,
            hidden: !customizeColumns.includes(binderReportColumns.createdBy.key),
        },
        {
            title: binderReportColumns.status.label,
            field: binderReportColumns.status.key,
            hidden: !customizeColumns.includes(binderReportColumns.status.key),
            defaultFilter: lodash.values(binderStatusLookups),
            lookup: binderStatusLookups,
            cellStyle: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textTransform: 'uppercase',
            },
        },
        {
            title: binderReportColumns.protocolNumber.label,
            field: binderReportColumns.protocolNumber.key,
            filterPlaceholder: binderReportColumns.protocolNumber.filter,
            hidden: !customizeColumns.includes(binderReportColumns.protocolNumber.key),
            emptyValue: <p className={styles.emptyLabel}>No P.N Provided</p>,
            cellStyle: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
        {
            title: binderReportColumns.primaryInvestigator.label,
            field: binderReportColumns.primaryInvestigator.key,
            filterPlaceholder: binderReportColumns.primaryInvestigator.filter,
            hidden: !customizeColumns.includes(binderReportColumns.primaryInvestigator.key),
            emptyValue: <p className={styles.emptyLabel}>No P.I Provided</p>,
        },
        {
            title: binderReportColumns.renewalDate.label,
            field: binderReportColumns.renewalDate.key,
            filterPlaceholder: binderReportColumns.renewalDate.filter,
            hidden: !customizeColumns.includes(binderReportColumns.renewalDate.key),
            emptyValue: <p className={styles.emptyLabel}>No R.D Provided</p>,
            cellStyle: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
            render: rowData => {
                return moment(rowData[binderReportColumns.renewalDate.key]).format(DATE_FORMAT);
            },
        },
        {
            title: binderReportColumns.irbNumber.label,
            field: binderReportColumns.irbNumber.key,
            filterPlaceholder: binderReportColumns.irbNumber.filter,
            hidden: !customizeColumns.includes(binderReportColumns.irbNumber.key),
            emptyValue: <p className={styles.emptyLabel}>No IRB Provided</p>,
        },
        {
            title: binderReportColumns.sponsorCro.label,
            field: binderReportColumns.sponsorCro.key,
            filterPlaceholder: binderReportColumns.sponsorCro.filter,
            hidden: !customizeColumns.includes(binderReportColumns.sponsorCro.key),
            emptyValue: <p className={styles.emptyLabel}>No Sponsor Provided</p>,
        },
        {
            title: binderReportColumns.binderForwardingAddress.label,
            field: binderReportColumns.binderForwardingAddress.key,
            filterPlaceholder: binderReportColumns.binderForwardingAddress.filter,
            hidden: !customizeColumns.includes(binderReportColumns.binderForwardingAddress.key),
            emptyValue: <p className={styles.emptyLabel}>No B.F.A Provided</p>,
        },
        {
            title: binderReportColumns.studyStage.label,
            field: binderReportColumns.studyStage.key,
            filterPlaceholder: binderReportColumns.studyStage.filter,
            hidden: !customizeColumns.includes(binderReportColumns.studyStage.key),
            emptyValue: <p className={styles.emptyLabel}>No study stage Provided</p>,
        },
    ];
};

export const reportBinderOptionData = {
    name: reportTypes.binder,
    displayName: 'Binder Reports',
    reportType: reportTypes.binder,
    parameters: [
        {
            id: 'institutionId',
            choice: 'institutionId',
            choiceLabel: 'Get a report on all binders for this Institution',
            isChoiceRequired: true,
            isChoiceHidden: true,
            label: 'Institution Id',
            type: null,
            source: null,
            dataKey: 'institutionId',
            target: 'institutionId',
            submit: true,
        },
        {
            id: 'all',
            choice: 'binderIds',
            choiceLabel: 'Choose the binders you would like to include in this report',
            isChoiceRequired: true,
            isChoiceHidden: false,
            label: 'Include all binders',
            type: null,
            source: null,
            target: null,
            submit: false,
        },
        {
            id: 'binderIds',
            choice: 'binderIds',
            choiceLabel: 'Choose a list of binders to restrict results to:',
            isChoiceRequired: false,
            label: 'Include only these binders',
            type: 'multiselect',
            source: 'binders',
            dataKey: 'binders',
            target: 'binderIds',
            targetKey: 'key',
            submit: true,
        },
        {
            id: statusKey,
            choice: 'binderIds',
            choiceLabel: 'Choose a list of binders to restrict results to:',
            isChoiceRequired: false,
            label: 'Include only these binder status',
            type: 'multiselect',
            source: statusKey,
            dataKey: statusKey,
            target: statusKey,
            targetKey: 'key',
            submit: true,
        },
    ],
};
